import "./styles.scss";

import { useState, useRef } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import useClickOutside from "libs/helpers/useClickOutside";
import useResponsive from "libs/helpers/useResponsive";
import useDropdownPlacement from "hooks/useDropdownPlacement";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as CheckIcon } from "assets/icons/new/check.svg";
import AvatarImage from "assets/images/avatar-placeholder.png";

const mainClass = "user-picker";

const UserPicker = ({ data, options, error, isDisabled, onChange }) => {
  const { t } = useTranslation();
  const { max } = useResponsive();

  const listRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(
    data?.map((item) => {
      const foundWebmaster = options.find((option) => option.id === item.id);

      if (!!foundWebmaster) return foundWebmaster;

      return { id: item.id, name: t("Deleted user") };
    }) || [],
  );

  useClickOutside(listRef, () => {
    setIsOpen(false);
  });

  const open_top = useDropdownPlacement(
    buttonRef,
    isOpen && !isDisabled && options?.length > 0,
    { dropdownRef, forceBottom: !max.sm },
  );

  return (
    <div
      className={classnames(mainClass, {
        [`${mainClass}--active`]: isOpen,
        [`${mainClass}--top`]: open_top,
        [`${mainClass}--single`]: selected?.length === 1,
        [`${mainClass}--all`]: selected?.length === options?.length,
        [`${mainClass}--error`]: error,
      })}
    >
      {selected?.length > 0 && (
        <ul>
          {selected.map((item, index) => (
            <li key={item.id} style={{ "--index": selected.length - index }}>
              <div className={`${mainClass}__person`}>
                <div>
                  <img src={item?.image || AvatarImage} alt="" />
                </div>
                <span>
                  {item?.name?.split(" ")[0]} <br />
                  {item?.name?.split(" ").slice(1).join(" ")}
                </span>
                {typeof item?.onClick === "function" && (
                  <button
                    className={`${mainClass}__person-redirect`}
                    type="button"
                    onClick={item?.onClick}
                  />
                )}
                {!isDisabled && (
                  <button
                    className={`${mainClass}__person-remove`}
                    type="button"
                    onClick={() => {
                      const newSelected = selected.filter(
                        (i) => i.id !== item.id,
                      );

                      setSelected(newSelected);

                      if (typeof onChange === "function") onChange(newSelected);
                    }}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {!isDisabled && (
        <div className={`${mainClass}__list`} ref={listRef}>
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setIsOpen((prevOpen) => !prevOpen)}
          >
            <i />
            {t("Add a person")}
            <span className="arrow">
              <ArrowIcon />
            </span>
          </button>
          {options?.length > 0 && isOpen && (
            <div ref={dropdownRef} className={`${mainClass}__list-options`}>
              {options.map((option) => {
                const isActive = selected?.find(
                  (item) => item.id === option.id,
                );

                return (
                  <button
                    key={option.id}
                    className={classnames({ active: isActive })}
                    type="button"
                    onClick={() => {
                      const newSelected = isActive
                        ? selected.filter((item) => item.id !== option.id)
                        : [...selected, option];

                      setSelected(newSelected);

                      if (typeof onChange === "function") onChange(newSelected);
                    }}
                  >
                    <i>
                      <img src={option?.image || AvatarImage} alt="" />
                    </i>
                    {option.name}
                    <span className="checkmark">
                      {selected?.find((item) => item.id === option.id) && (
                        <CheckIcon />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      {error && <div className={`${mainClass}__error`}>{error}</div>}
    </div>
  );
};

export default UserPicker;
